import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '@/lib/axios';
import LoanOfferCard from '@/components/LoanOfferCard';
import { Button } from "@/components/ui/button";
import { useLoader } from "@/contexts/LoaderContext";
import { toast } from "sonner";
import { API_BASE_URL } from '@/config';
import { ChevronRight, ArrowLeft } from 'lucide-react';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface Offer {
    id: number;
    loan_amount: string | number;
    tenure: number;
    interest_rate: string | number;
    emi: string | number;
    processing_fee: string | number;
    display_flag: boolean;
    display_order: number | null;
    offer_selected: boolean;
    rejected_by_le: boolean;
}

interface Lender {
    lender_id: number;
    lender_name: string;
    lender_logo?: string;
    lender_description?: string;
    lender_terms?: string;
    offers: Offer[];
}

const AllOffers: React.FC = () => {
    const navigate = useNavigate();
    const { setLoading } = useLoader();
    const showLoader = () => setLoading(true);
    const hideLoader = () => setLoading(false);

    const [lenders, setLenders] = useState<Lender[]>([]);
    const [sortBy, setSortBy] = useState<string>("alphabetical");
    const [selectedOfferId, setSelectedOfferId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchOffers();
    }, []);

    // Helper to extract numeric value safely
    const getNum = (val: string | number) => typeof val === 'string' ? parseFloat(val) : val;

    // Sorting Logic
    const sortedLenders = React.useMemo(() => {
        if (!lenders.length) return [];

        // Deep copy to avoid mutating original state during sort
        const lendersCopy = JSON.parse(JSON.stringify(lenders)) as Lender[];

        lendersCopy.forEach(lender => {
            // Sort offers within each lender first
            lender.offers.sort((a, b) => {
                // RULE 1: Rejected offers ALWAYS go last
                if (a.rejected_by_le !== b.rejected_by_le) {
                    return a.rejected_by_le ? 1 : -1;
                }

                // RULE 2: If both are same (both rejected or both not), use display_order if available
                if (a.display_order !== null && b.display_order !== null) {
                    if (a.display_order !== b.display_order) {
                        return a.display_order - b.display_order;
                    }
                } else if (a.display_order !== null) {
                    return -1; // a has order, b doesn't -> a first
                } else if (b.display_order !== null) {
                    return 1; // b has order, a doesn't -> b first
                }

                // RULE 3: Fallback to existing sort criteria
                const amountA = getNum(a.loan_amount);
                const amountB = getNum(b.loan_amount);
                const roiA = getNum(a.interest_rate);
                const roiB = getNum(b.interest_rate);

                switch (sortBy) {
                    case 'amount': return amountB - amountA; // Descending
                    case 'roi': return roiA - roiB; // Ascending
                    case 'alphabetical':
                    default: return amountB - amountA; // Default offers to Loan Amount Desc
                }
            });
        });

        // Sort Lenders
        lendersCopy.sort((a, b) => {
            // Helpers to get aggergate values for lender comparison using the *first* offer (which is now sorted)
            const bestA = a.offers[0];
            const bestB = b.offers[0];

            if (!bestA || !bestB) return 0;

            switch (sortBy) {
                case 'alphabetical':
                    return a.lender_name.localeCompare(b.lender_name);
                case 'amount':
                    // Max loan amount (which is now at index 0)
                    return getNum(bestB.loan_amount) - getNum(bestA.loan_amount);
                case 'roi':
                    return getNum(bestA.interest_rate) - getNum(bestB.interest_rate);
                default:
                    return 0;
            }
        });

        return lendersCopy;
    }, [lenders, sortBy]);

    const fetchOffers = async () => {
        showLoader();
        setError(null);
        try {
            const response = await axios.post(`${API_BASE_URL}/api/get_all_available_offers/`);
            if (response.data && response.data.lenders && response.data.lenders.length > 0) {
                const lendersData = response.data.lenders;
                setLenders(lendersData);

                // Check if ALL offers are rejected
                let allOffersRejected = true;
                let hasAnyOffer = false;

                lendersData.forEach((l: Lender) => {
                    l.offers.forEach((o: Offer) => {
                        hasAnyOffer = true;
                        if (!o.rejected_by_le) {
                            allOffersRejected = false;
                        }
                    });
                });

                if (allOffersRejected) {
                    navigate('/final-reject-state');
                    return;
                }

                // Auto-select if a user has already selected one
                let preSelectedId = null;
                lendersData.forEach((l: Lender) => {
                    l.offers.forEach((o: Offer) => {
                        if (o.offer_selected && !o.rejected_by_le) preSelectedId = o.id;
                    });
                });
                if (preSelectedId) setSelectedOfferId(preSelectedId);
            } else {
                setLenders([]); // No offers or empty response
                navigate('/final-reject-state');
            }
        } catch (err) {
            console.error("Failed to fetch offers", err);
            setError("Failed to load offers. Please try creating a new session or refresh.");
            toast.error("Something went wrong while fetching offers.");
        } finally {
            hideLoader();
        }
    };

    const handleSelectOffer = (id: number) => {
        const selectedLender = lenders.find(l => l.offers.some(o => o.id === id));
        const selectedOffer = selectedLender?.offers.find(o => o.id === id);

        if (selectedOffer?.rejected_by_le) {
            toast.error("This offer has been rejected and cannot be selected.");
            return;
        }

        setSelectedOfferId(id);
    };

    const handleProceed = async () => {
        if (!selectedOfferId) {
            toast.warning("Please select an offer to proceed.");
            return;
        }

        showLoader();
        try {
            const response = await axios.post(`${API_BASE_URL}/api/select_loan_offer/`, {
                offer_id: selectedOfferId
            });

            const nextPage = response.data?.next_page_url;
            if (nextPage) {
                navigate(nextPage);
            } else {
                navigate('/loan-offer'); // Fallback
            }

        } catch (err) {
            console.error("Failed to select offer", err);
            toast.error("Failed to save your selection. Please try again.");
        } finally {
            hideLoader();
        }
    };

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
                <div className="text-center space-y-4 max-w-md">
                    <p className="text-red-500 font-semibold">{error}</p>
                    <Button onClick={fetchOffers} variant="outline" className="w-full">
                        Retry Loading Offers
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50/50 pb-20">
            {/* Header */}
            <div className="bg-white border-b sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
                    <Button variant="ghost" size="icon" className="-ml-2" onClick={() => navigate(-1)}>
                        <ArrowLeft className="w-5 h-5 text-gray-600" />
                    </Button>
                    <h1 className="font-semibold text-lg text-gray-800">Available Offers</h1>
                    <div className="w-8" /> {/* Spacer for centering */}
                </div>
            </div>

            <main className="max-w-4xl mx-auto px-4 py-8">
                <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                    <div className="text-center sm:text-left">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose your loan offer</h2>
                        <p className="text-gray-500">We found the following exclusive offers for you based on your profile.</p>
                    </div>
                    <div className="w-full sm:w-[200px]">
                        <Select value={sortBy} onValueChange={setSortBy}>
                            <SelectTrigger>
                                <SelectValue placeholder="Sort by" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="alphabetical">Bank Name (A-Z)</SelectItem>
                                <SelectItem value="amount">Loan Amount</SelectItem>
                                <SelectItem value="roi">Interest Rate </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="space-y-6">
                    {sortedLenders.length > 0 ? (
                        sortedLenders.map((lender) => (
                            <LoanOfferCard
                                key={lender.lender_id}
                                lender={lender}
                                selectedOfferId={selectedOfferId}
                                onSelectOffer={handleSelectOffer}
                            />
                        ))
                    ) : (
                        <div className="text-center py-20 bg-white rounded-xl border border-dashed">
                            <p className="text-gray-500">No offers available at the moment.</p>
                        </div>
                    )}
                </div>
            </main>

            {/* Footer Action */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 shadow-[0_-4px_10px_rgba(0,0,0,0.05)] z-20">
                <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
                    <div className="hidden sm:block">
                        <p className="text-sm text-gray-500">
                            {selectedOfferId ? "Offer selected" : "Please select an offer to continue"}
                        </p>
                    </div>
                    <Button
                        onClick={handleProceed}
                        className="w-full sm:w-auto min-w-[200px] h-11 text-base shadow-lg hover:shadow-xl transition-all"
                        disabled={!selectedOfferId}
                    >
                        Proceed to Next Step <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default AllOffers;
